import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Edit, Trash2, Search, User, X, Building2, FileText, Shield, Crown, ChevronLeft, ChevronRight, Eye, EyeOff } from 'lucide-react';
import Header from '../../components/Header';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { getRoleLabel, getRoleColor, getRoleIcon } from '../../utils/permissions';

const initialUsers = [];

const UserManagementPage = () => {
  const { t } = useTranslation();
  const { isGlobalAdmin, isCompanyAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);

  // Función para renderizar badge de rol global
  const renderGlobalRoleBadge = (role) => {
    const colorClass = getRoleColor(role);
    const icon = getRoleIcon(role);
    const label = getRoleLabel(role);
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colorClass}`}>
        <span>{icon}</span>
        <span>{label}</span>
      </span>
    );
  };

  // Función para renderizar badge de rol en empresa
  const renderCompanyRoleBadge = (role) => {
    const colors = {
      'admin': 'bg-blue-50 text-blue-700 border-blue-200',
      'agent': 'bg-green-50 text-green-700 border-green-200',
      'viewer': 'bg-gray-50 text-gray-700 border-gray-200'
    };
    const icons = {
      'admin': '⚙️',
      'agent': '👤',
      'viewer': '👁️'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border ${colors[role] || colors.agent}`}>
        <span>{icons[role] || '📋'}</span>
        <span>{role}</span>
      </span>
    );
  };
  const [users, setUsers] = useState(initialUsers);
  const [companies, setCompanies] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedAgencyType, setSelectedAgencyType] = useState('all');

  // Modal / form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '', email: '', username: '', password: '', avatar: '', country: '', agency: '', agencyType: 'internal', cartaOperativaUrl: '', role: 'agent'
  });
  
  // File upload states
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [cartaOperativaFile, setCartaOperativaFile] = useState(null);
  const [clearAvatar, setClearAvatar] = useState(false);
  const [clearCartaOperativa, setClearCartaOperativa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyRoles, setCompanyRoles] = useState({});

  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, companiesRes] = await Promise.all([
        apiService.getUsers(),
        apiService.getCompanies()
      ]);

      setUsers(usersRes || []);
      // fallback if backend returns empty
      const comps = (companiesRes && companiesRes.length) ? companiesRes : [
        { id: 'chirakChile', name: 'Chirak Turismo Chile', country: 'Chile' },
        { id: 'chirakPeru', name: 'Chirak Turismo Perú', country: 'Perú' }
      ];
      setCompanies(comps);
    } catch (err) {
      console.error('Error loading data:', err);
      setUsers(initialUsers);
      setCompanies([
        { id: 'chirakChile', name: 'Chirak Turismo Chile', country: 'Chile' },
        { id: 'chirakPeru', name: 'Chirak Turismo Perú', country: 'Perú' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsers = () => {
    return users.filter(user => {
      const matchesSearch = (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.agency || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCompany = selectedCompany === 'all' || (user.companies || []).some(c => (c.company_id || c.id) === selectedCompany);
      const matchesAgency = selectedAgencyType === 'all' || user.agency_type === selectedAgencyType || user.agencyType === selectedAgencyType;

      return matchesSearch && matchesCompany && matchesAgency;
    });
  };
  
  // Calcular usuarios para la página actual
  const getPaginatedUsers = () => {
    const filtered = getFilteredUsers();
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return filtered.slice(indexOfFirstUser, indexOfLastUser);
  };
  
  const totalPages = Math.ceil(getFilteredUsers().length / usersPerPage);
  
  // Reset página al cambiar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany, selectedAgencyType]);

  const handleAddUser = () => {
    setEditingUser(null);
    setNewUser({ name: '', email: '', username: '', password: '', avatar: '', country: '', agency: '', agencyType: 'internal', cartaOperativaUrl: '', role: 'agent' });
    setSelectedCompanies([]);
    setCompanyRoles({});
    setAvatarFile(null);
    setAvatarPreview('');
    setCartaOperativaFile(null);
    setClearAvatar(false);
    setClearCartaOperativa(false);
    setIsModalOpen(true);
  };

  const getUserCompanies = (user) => user.companies || [];

  const handleEditUser = (user) => {
    setEditingUser(user);
    setNewUser({
      name: user.name || '',
      email: user.email || '',
      username: user.username || '',
      password: '',
      avatar: user.avatar || '',
      country: user.country || '',
      agency: user.agency || '',
      agencyType: user.agency_type || user.agencyType || 'internal',
      cartaOperativaUrl: user.carta_operativa || user.cartaOperativaUrl || '',
      role: user.role || 'agent'
    });

    // Resetear archivos y preview
    setAvatarFile(null);
    setAvatarPreview(user.avatar || '');
    setCartaOperativaFile(null);
    setClearAvatar(false);
    setClearCartaOperativa(false);

    const userCompanies = getUserCompanies(user);
    const selectedIds = userCompanies.map(c => c.company_id || c.id);
    setSelectedCompanies(selectedIds);

    const roles = {};
    userCompanies.forEach(c => {
      const cid = c.company_id || c.id;
      roles[cid] = c.role_in_company || c.role || 'agent';
    });
    setCompanyRoles(roles);

    setIsModalOpen(true);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCartaOperativaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCartaOperativaFile(file);
      setClearCartaOperativa(false);
    }
  };

  const handleClearAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview('');
    setClearAvatar(true);
  };

  const handleClearCartaOperativa = () => {
    setCartaOperativaFile(null);
    setClearCartaOperativa(true);
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    if (avatar.startsWith('http')) return avatar;
    if (avatar.startsWith('data:')) return avatar; // Base64 preview
    return `${apiService.baseURL}${avatar}`; // URL del servidor
  };

  const getCartaOperativaUrl = (carta) => {
    if (!carta) return null;
    if (carta.startsWith('http')) return carta;
    return `${apiService.baseURL}${carta}`;
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies(prev => {
      const exists = prev.includes(companyId);
      const next = exists ? prev.filter(id => id !== companyId) : [...prev, companyId];

      if (!next.includes(companyId)) {
        setCompanyRoles(prevRoles => {
          const nr = { ...prevRoles };
          delete nr[companyId];
          return nr;
        });
      } else if (!companyRoles[companyId]) {
        setCompanyRoles(prevRoles => ({ ...prevRoles, [companyId]: 'agent' }));
      }

      return next;
    });
  };

  const handleRoleChange = (companyId, role) => {
    setCompanyRoles(prev => ({ ...prev, [companyId]: role }));
  };

  const handleSaveUser = async () => {
    // Permitir creación a global_admin Y company_admin
    if (!isGlobalAdmin && !isCompanyAdmin) {
      alert('No tienes permisos para crear o editar usuarios. Necesitas ser admin.');
      return;
    }

    try {
      if (!newUser.name.trim() || !newUser.email.trim()) {
        alert('Nombre y email son requeridos');
        return;
      }

      if (!editingUser && !newUser.password.trim()) {
        alert('Contraseña requerida para usuario nuevo');
        return;
      }

      if (selectedCompanies.length === 0) {
        alert('Debes asignar al menos una empresa al usuario.');
        return;
      }

      setLoading(true);

      // Subir archivos si hay alguno seleccionado
      let avatarUrl = clearAvatar ? '' : (editingUser?.avatar || newUser.avatar || '');
      let cartaOperativaUrl = clearCartaOperativa ? '' : (editingUser?.carta_operativa || '');

      console.log('🖼️ Avatar inicial:', { avatarUrl, editingUser: editingUser?.avatar, newUser: newUser.avatar });

      if (avatarFile) {
        try {
          console.log('🔄 Intentando subir avatar:', {
            name: avatarFile.name,
            type: avatarFile.type,
            size: avatarFile.size
          });
          const uploadResult = await apiService.uploadFile(avatarFile, 'avatars');
          console.log('✅ Avatar subido exitosamente:', uploadResult);
          avatarUrl = uploadResult.url;
        } catch (err) {
          console.error('❌ Error completo al subir avatar:', {
            error: err,
            message: err.message,
            stack: err.stack,
            response: err.response
          });
          alert(`Error al subir el avatar: ${err.message || 'Error desconocido'}. Continuando sin actualizar...`);
        }
      }

      if (cartaOperativaFile) {
        try {
          console.log('🔄 Intentando subir carta operativa:', {
            name: cartaOperativaFile.name,
            type: cartaOperativaFile.type,
            size: cartaOperativaFile.size
          });
          const uploadResult = await apiService.uploadFile(cartaOperativaFile, 'cartas');
          console.log('✅ Carta operativa subida exitosamente:', uploadResult);
          cartaOperativaUrl = uploadResult.url;
        } catch (err) {
          console.error('❌ Error completo al subir carta operativa:', {
            error: err,
            message: err.message,
            stack: err.stack,
            response: err.response
          });
          alert(`Error al subir la carta operativa: ${err.message || 'Error desconocido'}. Continuando sin actualizar...`);
        }
      }

      const payload = {
        name: newUser.name,
        email: newUser.email,
        username: newUser.username && newUser.username.trim() ? newUser.username.trim() : null,
        password: newUser.password,
        avatar: avatarUrl,
        carta_operativa: cartaOperativaUrl,
        country: newUser.country,
        agency: newUser.agency,
        agency_type: newUser.agencyType, // Normalizar para backend
        role: newUser.role || 'agent', // Agregar rol global
        companies: selectedCompanies.map(cid => ({ company_id: cid, role_in_company: companyRoles[cid] || 'agent' }))
      };

      console.log('📦 Payload antes de limpiar:', JSON.stringify(payload, null, 2));

      // Eliminar password si está vacío o solo espacios
      if (editingUser && (!payload.password || payload.password.trim() === '')) {
        delete payload.password;
      }

      console.log('Payload enviado a updateUser:', payload);
      if (editingUser) {
        await apiService.updateUser(editingUser.id, payload);
        setSuccessMessage('Usuario actualizado correctamente');
      } else {
        await apiService.createUser(payload);
        setSuccessMessage('Usuario creado correctamente');
      }

      setIsModalOpen(false);
      setShowPassword(false); // Resetear visibilidad de contraseña al cerrar
      await loadData(); // Recargar datos después de cerrar modal
      setLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving user', err);
      alert('Error al guardar usuario');
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!isGlobalAdmin) {
      alert('No tienes permisos para eliminar usuarios');
      return;
    }

    // Abrir modal de confirmación
    const user = users.find(u => u.id === id);
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = async (permanent = false) => {
    if (!userToDelete) return;

    try {
      await apiService.deleteUser(userToDelete.id, permanent);
      setSuccessMessage(permanent ? 'Usuario eliminado permanentemente' : 'Usuario desactivado');
      setDeleteModalOpen(false);
      setUserToDelete(null);
      await loadData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting user', err);
      alert('Error al eliminar usuario');
      setDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleToggleUserActive = async (id, current) => {
    if (!isGlobalAdmin) {
      alert('No tienes permisos para cambiar el estado de los usuarios');
      return;
    }

    try {
      await apiService.patchUserStatus(id, { is_active: !current });
      setSuccessMessage(current ? 'Usuario desactivado' : 'Usuario activado');
      await loadData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(err);
      alert('Error cambiando estado');
    }
  };

  const handleRemoveCompanyAccess = async (userId, companyId) => {
    if (!isGlobalAdmin) {
      alert('No tienes permisos para modificar accesos de usuarios');
      return;
    }

    if (!window.confirm('Remover acceso a la empresa?')) return;
    try {
      const user = users.find(u => u.id === userId);
      const updated = (user.companies || []).filter(c => (c.company_id || c.id) !== companyId);
      await apiService.updateUser(userId, { companies: updated });
      await loadData();
    } catch (err) {
      console.error(err);
      alert('Error al remover acceso');
    }
  };

  const filteredUsers = getPaginatedUsers();
  const totalUsers = getFilteredUsers().length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />

      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-28 w-28 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <User className="w-8 h-8 text-blue-600" />
                  {t('user.title')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Administra usuarios y sus accesos • {totalUsers} usuario{totalUsers !== 1 ? 's' : ''} {searchTerm || selectedCompany !== 'all' ? 'filtrados' : 'totales'}
                </p>
              </div>
              <button 
                onClick={handleAddUser} 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 hover:shadow-lg transition-all"
              >
                <UserPlus className="w-5 h-5" /> {t('user.add')}
              </button>
            </div>
          </motion.div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" /> {t('common.filter')}
            </h3>
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => setSelectedCompany('all')} className={`px-3 py-1 rounded ${selectedCompany === 'all' ? 'bg-blue-600 text-white' : 'bg-white'}`}>
                {t('common.all') || 'Todos'}
              </button>
              {companies.map(c => (
                <button key={c.id} onClick={() => setSelectedCompany(c.id)} className={`px-3 py-1 rounded ${selectedCompany === c.id ? 'bg-blue-600 text-white' : 'bg-white'}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {successMessage && <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">{successMessage}</div>}

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="relative flex-1 min-w-[250px]">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    placeholder={`${t('common.search')} por nombre, email, usuario o agencia...`} 
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Mostrar:</span>
                  <select 
                    value={usersPerPage} 
                    onChange={e => {
                      setUsersPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('user.name')}</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('user.email')}</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('user.role')}</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('user.agency')}</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('user.companies')}</th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-12 text-center text-gray-500">
                        <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg">No se encontraron usuarios</p>
                        <p className="text-sm mt-1">Intenta ajustar los filtros de búsqueda</p>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user, idx) => (
                      <tr key={user.id || idx} className="hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <img 
                              src={getAvatarUrl(user.avatar) || `https://api.adorable.io/avatars/40/${user.email || idx}.png`} 
                              alt="avatar" 
                              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200" 
                            />
                            <div>
                              <div className="font-medium text-gray-900">{user.name}</div>
                              {user.username && (
                                <div className="text-xs text-gray-500">@{user.username}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm text-gray-700">{user.email}</div>
                          {!user.is_active && (
                            <span className="text-xs text-red-600 font-medium">● {t('common.inactive')}</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {renderGlobalRoleBadge(user.role)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm space-y-1">
                            {user.country && <div className="text-gray-700 font-medium">{user.country}</div>}
                            {user.agency && <div className="text-xs text-gray-500 truncate max-w-[200px]" title={user.agency}>{user.agency}</div>}
                            {user.agency_type === 'external' && (
                              <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">Externa</span>
                            )}
                            {user.carta_operativa && (
                              <a 
                                href={getCartaOperativaUrl(user.carta_operativa)} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                              >
                                <FileText className="w-3 h-3" /> Carta
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {(user.companies || []).length === 0 ? (
                              <span className="text-xs text-gray-400 italic">{t('user.requiresCompany')}</span>
                            ) : (
                              (user.companies || []).map(c => (
                                <div key={c.company_id || c.id} className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
                                  <Building2 className="w-3 h-3 text-gray-400" />
                                  <span className="font-medium text-gray-700">{c.name || c.company_id || c.id}</span>
                                  {renderCompanyRoleBadge(c.role_in_company || 'agent')}
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center gap-1">
                            <button 
                              onClick={() => handleEditUser(user)} 
                              className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                              title="Editar usuario"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {isGlobalAdmin && (
                              <button 
                                onClick={() => handleDeleteUser(user.id)} 
                                className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                title="Eliminar usuario"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <button 
                              onClick={() => handleToggleUserActive(user.id, user.is_active)} 
                              className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                                user.is_active 
                                  ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                              }`}
                              title={user.is_active ? 'Desactivar usuario' : 'Activar usuario'}
                            >
                              {user.is_active ? '✓' : '○'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Controles de paginación */}
            {totalUsers > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{Math.min((currentPage - 1) * usersPerPage + 1, totalUsers)}</span> - <span className="font-medium">{Math.min(currentPage * usersPerPage, totalUsers)}</span> de <span className="font-medium">{totalUsers}</span> usuarios
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="perPage" className="text-sm text-gray-600">Por página:</label>
                    <select
                      id="perPage"
                      value={usersPerPage}
                      onChange={(e) => {
                        setUsersPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg border ${
                      currentPage === 1
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                    } transition-colors`}
                    title="Página anterior"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const showPages = 5; // Número de páginas a mostrar
                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let endPage = Math.min(totalPages, startPage + showPages - 1);
                      
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }
                      
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setCurrentPage(1)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(<span key="dots1" className="px-2 text-gray-500">...</span>);
                        }
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                              currentPage === i
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="dots2" className="px-2 text-gray-500">...</span>);
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            {totalPages}
                          </button>
                        );
                      }
                      
                      return pages;
                    })()}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg border ${
                      currentPage === totalPages
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                    } transition-colors`}
                    title="Página siguiente"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Modal para agregar/editar usuario */}
          <AnimatePresence>
            {isModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{editingUser ? t('user.edit') : t('user.add')}</h3>
                      {!isGlobalAdmin && (
                        <p className="text-xs text-amber-600 mt-1">⚠️ {t('user.noPermissions')}</p>
                      )}
                    </div>
                    <button onClick={() => setIsModalOpen(false)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
                  </div>

                  {/* Info panel sobre roles */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-blue-900">
                        <div className="font-medium mb-1">💡 Sistema de Roles</div>
                        <div className="space-y-1 text-blue-800">
                          <div><span className="font-medium">Rol Global:</span> Define el nivel de acceso general del usuario</div>
                          <div><span className="font-medium">Rol por Empresa:</span> Define qué puede hacer en cada empresa específica</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm">{t('user.name')}</label>
                        <input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="w-full border px-3 py-2 rounded" />
                      </div>
                      <div>
                        <label className="block text-sm">{t('user.email')}</label>
                        <input value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="w-full border px-3 py-2 rounded" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm">{t('user.username')}</label>
                        <input 
                          value={newUser.username} 
                          onChange={e => setNewUser({...newUser, username: e.target.value})} 
                          className="w-full border px-3 py-2 rounded" 
                          placeholder="nombre.usuario o usuario123"
                        />
                        <p className="text-xs text-gray-500 mt-1">Para login alternativo al email</p>
                      </div>
                      <div>
                        <label className="block text-sm">{t('user.password')}</label>
                        <div className="relative">
                          <input 
                            type={showPassword ? "text" : "password"} 
                            value={newUser.password} 
                            onChange={e => setNewUser({...newUser, password: e.target.value})} 
                            className="w-full border px-3 py-2 pr-10 rounded" 
                            placeholder={editingUser ? 'Dejar vacío para mantener la actual' : ''} 
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm">{t('user.country')}</label>
                        <input value={newUser.country} onChange={e => setNewUser({...newUser, country: e.target.value})} className="w-full border px-3 py-2 rounded" placeholder="Chile, Perú, etc." />
                      </div>
                      <div>
                        <label className="block text-sm">{t('user.agency')}</label>
                        <input value={newUser.agency} onChange={e => setNewUser({...newUser, agency: e.target.value})} className="w-full border px-3 py-2 rounded" placeholder={t('user.agency')} />
                      </div>
                    </div>

                    {/* Selector de rol global (solo para global admin) */}
                    {isGlobalAdmin && (
                      <div className="border-t pt-4">
                        <label className="flex items-center gap-2 text-sm font-medium mb-2">
                          <Crown className="w-4 h-4 text-purple-600" />
                          {t('user.role')}
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <label className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input 
                              type="radio" 
                              name="global-role" 
                              value="global_admin"
                              checked={newUser.role === 'global_admin'} 
                              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm flex items-center gap-1">
                                👑 Global Admin
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                Acceso total al sistema
                              </div>
                            </div>
                          </label>
                          <label className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input 
                              type="radio" 
                              name="global-role" 
                              value="company_admin"
                              checked={newUser.role === 'company_admin'} 
                              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm flex items-center gap-1">
                                🔧 Company Admin
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                Deprecado - usar rol por empresa
                              </div>
                            </div>
                          </label>
                          <label className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors bg-blue-50 border-blue-300">
                            <input 
                              type="radio" 
                              name="global-role" 
                              value="agent"
                              checked={!newUser.role || newUser.role === 'agent'} 
                              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm flex items-center gap-1">
                                👤 Agent
                              </div>
                              <div className="text-xs text-blue-700 mt-1">
                                Recomendado para todos
                              </div>
                            </div>
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          💡 <strong>Recomendación:</strong> Usa "Agent" como rol global y asigna "Admin" en las empresas específicas para delegar administración.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm mb-1">Foto o Logo Usuario</label>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="w-full border px-3 py-2 rounded text-sm" />
                        {avatarPreview && !clearAvatar && (
                          <div className="mt-2 flex items-center gap-2">
                            <img src={getAvatarUrl(avatarPreview)} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-blue-200" />
                            <button
                              type="button"
                              onClick={handleClearAvatar}
                              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
                              title="Eliminar avatar"
                            >
                              <X className="w-3 h-3" /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm mb-1">Carta Operativa (PDF)</label>
                        <input type="file" accept=".pdf" onChange={handleCartaOperativaChange} className="w-full border px-3 py-2 rounded text-sm" />
                        {cartaOperativaFile && (
                          <div className="mt-2 text-xs text-green-600 flex items-center gap-2">
                            <FileText className="w-3 h-3" />
                            <span className="truncate max-w-[150px]">{cartaOperativaFile.name}</span>
                            <button
                              type="button"
                              onClick={handleClearCartaOperativa}
                              className="flex items-center gap-1 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
                              title="Eliminar carta operativa"
                            >
                              <X className="w-3 h-3" /> Eliminar
                            </button>
                          </div>
                        )}
                        {editingUser && editingUser.carta_operativa && !cartaOperativaFile && !clearCartaOperativa && (
                          <div className="mt-2 flex items-center gap-2">
                            <a 
                              href={getCartaOperativaUrl(editingUser.carta_operativa)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <FileText className="w-3 h-3" />
                              Ver carta operativa actual
                            </a>
                            <button
                              type="button"
                              onClick={handleClearCartaOperativa}
                              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
                              title="Eliminar carta operativa"
                            >
                              <X className="w-3 h-3" /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <Building2 className="w-4 h-4 text-blue-600" />
                          Empresas Asignadas & Roles
                        </label>
                        <span className="text-xs text-gray-600">
                          {selectedCompanies.length} empresa{selectedCompanies.length !== 1 ? 's' : ''} seleccionada{selectedCompanies.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {companies.length === 0 ? (
                        <div className="py-4 text-center text-sm text-blue-600">Cargando empresas...</div>
                      ) : (
                        <div className="space-y-3">
                          {/* Nota informativa */}
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                            <div className="font-medium mb-1">💡 Selecciona el rol para cada empresa:</div>
                            <div className="space-y-1 text-amber-800">
                              <div>• <strong>Admin:</strong> Gestiona productos, categorías y usuarios de la empresa</div>
                              <div>• <strong>Agente:</strong> Ve tarifas y genera PDFs de cotización</div>
                              <div>• <strong>Viewer:</strong> Solo puede consultar tarifas (no genera PDFs)</div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">{companies.map(company => (
                            <div key={company.id} className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedCompanies.includes(company.id) ? 'bg-blue-50 border-blue-400 shadow-sm' : 'bg-white hover:border-gray-400'}`} onClick={() => handleCompanyToggle(company.id)}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <input 
                                    type="checkbox" 
                                    checked={selectedCompanies.includes(company.id)} 
                                    onChange={() => {}} 
                                    onClick={(e) => e.stopPropagation()} 
                                    className="w-4 h-4 text-blue-600"
                                  />
                                  <div>
                                    <div className="font-medium text-gray-900">{company.name}</div>
                                    <div className="text-xs text-gray-500">{company.country || 'Sin país especificado'}</div>
                                  </div>
                                </div>
                                {selectedCompanies.includes(company.id) && (
                                  <div className="flex items-center gap-1">
                                    {renderCompanyRoleBadge(companyRoles[company.id] || 'agent')}
                                  </div>
                                )}
                              </div>
                              {selectedCompanies.includes(company.id) && (
                                <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                                  <div className="text-xs font-medium text-gray-700 mb-1">Rol en esta empresa:</div>
                                  <div className="flex flex-col gap-2">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
                                      <input type="radio" name={`role-${company.id}`} checked={companyRoles[company.id] === 'admin'} onChange={() => handleRoleChange(company.id, 'admin')} className="text-blue-600" />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-1 font-medium">⚙️ Admin</div>
                                        <div className="text-xs text-gray-500">Gestiona productos, categorías y usuarios</div>
                                      </div>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
                                      <input type="radio" name={`role-${company.id}`} checked={companyRoles[company.id] === 'agent'} onChange={() => handleRoleChange(company.id, 'agent')} className="text-green-600" />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-1 font-medium">👤 Agente</div>
                                        <div className="text-xs text-gray-500">Ve tarifas y genera PDFs</div>
                                      </div>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
                                      <input type="radio" name={`role-${company.id}`} checked={companyRoles[company.id] === 'viewer'} onChange={() => handleRoleChange(company.id, 'viewer')} className="text-gray-600" />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-1 font-medium">👁️ Viewer</div>
                                        <div className="text-xs text-gray-500">Solo puede ver tarifas (no genera PDFs)</div>
                                      </div>
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}</div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3 mt-4">
                      <button onClick={() => setIsModalOpen(false)} className="flex-1 px-4 py-2 border rounded hover:bg-gray-100">{t('common.cancel')}</button>
                      <button onClick={handleSaveUser} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{editingUser ? t('common.save') : t('common.add')}</button>
                    </div>
                  </div> {/* Cierra space-y-3 */}
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Modal de Confirmación de Eliminación */}
          <AnimatePresence>
            {deleteModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-red-100 rounded-full p-3">
                      <Trash2 className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Eliminar Usuario
                      </h3>
                      <p className="text-sm text-gray-600">
                        {userToDelete?.name} ({userToDelete?.email})
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 font-medium mb-2">
                        ⚠️ Elige una opción:
                      </p>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li><strong>Desactivar:</strong> El usuario no podrá iniciar sesión pero sus datos se mantienen</li>
                        <li><strong>Eliminar Permanentemente:</strong> Se borra completamente de la base de datos (irreversible)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => confirmDeleteUser(false)}
                      className="w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <span>⚠️</span>
                      Solo Desactivar (Recomendado)
                    </button>
                    <button
                      onClick={() => confirmDeleteUser(true)}
                      className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Eliminar Permanentemente
                    </button>
                    <button
                      onClick={() => {
                        setDeleteModalOpen(false);
                        setUserToDelete(null);
                      }}
                      className="w-full px-4 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;